import { Injectable } from '@angular/core';
import { fromDateOnlyString } from '../../../core/date';
import { AdditionalParameterDefinition, ParameterValueType, REPORT_PARAMETER_REGISTRY, ReportParameterDefinition } from '.';
import { ReportDefinition } from '../reporting.model';
import { Params } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class ReportParametersService {
    /**
     * Builds the final parameter definitions for the report.
     *
     * Steps:
     * 1. Merge each report parameter’s base registry definition with its overrides.
     * 2. Inject default SubscriberKey (overridden by query params if provided).
     * 3. Apply query-param defaults for keys in the form `p_<parameterKey>`.
     * 4. Normalize any date-period-related defaults.
     *
     * Returns the complete set of resolved parameter definitions for the report.
     */
    getParameterDefinitions(reportDefinition: ReportDefinition) {
        /* Merge each parameter's base definition from the registry with its report-specific overrides */
        return reportDefinition.parameters.map((parameterConfig) => {
            const reportParameterDefinition = REPORT_PARAMETER_REGISTRY[parameterConfig.key];

            let options: unknown;
            if ('options' in reportParameterDefinition) {
                const baseOptions = reportParameterDefinition.options;

                if (parameterConfig.overrides?.options != null) {
                    options = { ...baseOptions, ...parameterConfig.overrides.options };
                } else {
                    options = baseOptions;
                }
            }

            return {
                ...reportParameterDefinition,
                ...parameterConfig.overrides,
                ...(options ? { options } : {})
            };
        });
    }

    /**
     * Extracts parameter default values from query parameters.
     * 
     * Recognizes query keys in the format `p_<parameterKey` and returns a 
     * map of parameterKey -> raw query value.
     *  
     * @param queryParams The current route query parameters.
     * @returns A map of parameter keys to raw string string[] values.
     */
    getParameterDefaultsFromQueryParams(queryParams: Params): Record<string, string | string[]> {
        const queryParameterKeys = Object.keys(queryParams).filter(k => k.startsWith('p_'));

        const defaultValueMap: Record<string, string | string[]> = {};

        for (const key of queryParameterKeys) {
            const rawValue = queryParams[key];
            const reportParameterKey = key.substring(2);

            defaultValueMap[reportParameterKey] = rawValue;
        }

        return defaultValueMap;
    }


    /**
     * Resolves the initial value for a parameter.
     * 
     * Precedence:
     * 1. Query parameter value (if present and successfully parsed)
     * 2. Parameter definition defaultValue
     * 
     * @param reportParameterDefinition The parameter definition
     * @param queryRawValue The raw value for this parameter extracted from query parameters. 
     * @returns The resolved initial value or null.
     */
    resolveInitialParameterValue(
        reportParameterDefinition: ReportParameterDefinition | AdditionalParameterDefinition, 
        queryRawValue: string | string[] | null
    ): unknown {
        if (queryRawValue!= null){
            const parsed = this.parseParameterValue(reportParameterDefinition.valueType, queryRawValue);
            if (parsed != null) {
                return parsed;
            }
        }

        return reportParameterDefinition.defaultValue ?? null;
    }

    /**
     * Parses a raw query parameter value into a strongly-typed parameter value.
     * 
     * Supports primitive and array types including number, string, boolean, and date.
     * 
     * Returns null when parsing fails or when the value type is unsupported.
     * @param parameterValueType The expected parameter value type.
     * @param raw The raw value from query parameters (string or string[])
     * @returns The parsed value or null if invalid.
     */
    parseParameterValue(parameterValueType: ParameterValueType, raw: string | string[]): unknown {
        const values = Array.isArray(raw) ? raw : [raw];
        switch (parameterValueType) {
            case 'number':
                const n = Number(values[0]);
                return isNaN(n) ? null : n;
            case 'number[]':
                return values
                    .map((r) => {
                        const n = Number(r);
                        return isNaN(n) ? null : n;
                    })
                    .filter((n) => n != null);
            case 'string':
                return values[0];
            case 'string[]':
                return values;
            case 'boolean':
                return values[0] === 'true';
            case 'date':
                return fromDateOnlyString(values[0]);
            default:
                return null;
        }
    }
}
